import {MultiSelect} from '@mantine/core';
import {Box} from 'src/design/atoms/Box';
import {Text} from 'src/design/atoms/Text';
import {Tooltip} from 'src/design/atoms/Tooltip';
import {DatePickerInput} from '@mantine/dates';
import dayjs from 'dayjs';
import fileDownload from 'js-file-download';
import type {MRT_ColumnDef} from 'mantine-react-table';
import {useMemo, useState} from 'react';
import type {
  AssistantFeedbackItemResponse,
  AssistantFeedbackReasonEnum,
} from 'src/api/ProactApiClient';
import {AssistantFeedbackRatingEnum} from 'src/api/ProactApiClient';
import {
  FEEDBACK_REASONS,
  feedbackReasonLabel,
} from 'src/app/assistant/Feedback/messageFeedback';
import {Pill} from 'src/design/atoms/Pill';
import {SegmentedControl} from 'src/design/atoms/SegmentedControl';
import {SelectBox} from 'src/design/atoms/SelectBox';
import {Textbox} from 'src/design/atoms/Textbox';
import {TableRowActions} from 'src/design/molecules/TableRowActions';
import {TableToolbar} from 'src/design/molecules/TableToolbar';
import {useTable, useTableState} from 'src/design/organisms/useTable';
import {
  useAssistantFeedbackQuery,
  useExportAssistantFeedbackMutation,
  useExportGlobalAssistantFeedbackMutation,
  useGlobalAssistantFeedbackQuery,
} from 'src/redux/queries/assistantControlCenter';
import {useAlert} from 'src/shared/hooks/useAlert';
import useDebounce from 'src/shared/hooks/useDebounce';
import {formatCount, formatUtcDateTime} from 'src/shared/utils/formatters';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import type {ISelectOption} from 'src/types/Form';
import {RATING_SLUGS, ratingFromSlug} from './controlCenterParams';
import {toUtcDayRange} from 'src/shared/utils/utcDate';
import {useFeedbackUrlFilters} from './useControlCenterUrlState';

export interface AssistantFeedbackTableProps {
  scope: 'instance' | 'global';
  /** Global page instance filter — narrows votes to one instance. */
  instanceId?: number;
  userOptions: ISelectOption[];
  showInstanceColumn?: boolean;
  onViewConversation: (conversationId: number, messageId: number) => void;
}

const EMPTY_ITEMS: AssistantFeedbackItemResponse[] = [];
const TOOLTIP_MAX_CHARS = 800;
const SEARCH_DEBOUNCE_MS = 300;
const RATING_ALL = 'all';


const clip = (text: string, max: number) =>
  text.length > max ? `${text.slice(0, max - 1)}…` : text;

const ClampedText = ({text}: {text: string | null | undefined}) =>
  text ? (
    <Tooltip
      label={clip(text, TOOLTIP_MAX_CHARS)}
      multiline
      w={420}
      openDelay={300}
      style={{whiteSpace: 'pre-wrap'}}>
      <Text
        size="sm"
        lineClamp={2}
        style={{whiteSpace: 'normal', wordBreak: 'break-word'}}>
        {text}
      </Text>
    </Tooltip>
  ) : null;

const exportFileName = (exportedAt: Date): string =>
  `scout-feedback-${dayjs(exportedAt).format('YYYY-MM-DD')}.csv`;

export const AssistantFeedbackTable = ({
  scope,
  instanceId,
  userOptions,
  showInstanceColumn,
  onViewConversation,
}: AssistantFeedbackTableProps) => {
  const {setAlert} = useAlert();
  const {rating, reasons, setRating, setReasons} = useFeedbackUrlFilters();
  const [userId, setUserId] = useState<number | undefined>(undefined);
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    null,
    null,
  ]);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_MS) as string;

  const columns = useMemo<MRT_ColumnDef<AssistantFeedbackItemResponse>[]>(
    () => [
      {
        accessorKey: 'rating',
        header: 'Rating',
        size: 110,
        Cell: ({row}) =>
          row.original.rating === AssistantFeedbackRatingEnum.Up ? (
            <Pill text="Up" color="green" />
          ) : (
            <Pill text="Down" color="red" />
          ),
      },
      {
        accessorKey: 'reason',
        header: 'Reason',
        size: 150,
        Cell: ({row}) => feedbackReasonLabel(row.original.reason),
      },
      {
        accessorKey: 'comment',
        header: 'Comment',
        Cell: ({row}) => <ClampedText text={row.original.comment} />,
      },
      {
        accessorKey: 'question',
        header: 'Question',
        Cell: ({row}) => <ClampedText text={row.original.question} />,
      },
      {
        accessorKey: 'answer',
        header: 'Answer',
        size: 260,
        Cell: ({row}) => <ClampedText text={row.original.answer} />,
      },
      {
        accessorKey: 'userName',
        header: 'User',
        size: 150,
        Cell: ({row}) => row.original.userName ?? '—',
      },
      ...(showInstanceColumn
        ? [
            {
              accessorKey: 'instanceName',
              header: 'Instance',
              size: 150,
              Cell: ({row}) => row.original.instanceName ?? '—',
            } as MRT_ColumnDef<AssistantFeedbackItemResponse>,
          ]
        : []),
      {
        accessorKey: 'votedAt',
        header: 'Voted',
        size: 170,
        Cell: ({row}) => formatUtcDateTime(row.original.votedAt),
      },
    ],
    [showInstanceColumn]
  );

  const tableState = useTableState({
    identifier: `assistant-feedback-${scope}`,
    columns,
    defaultDensity: 'xs',
    defaultPagination: {pageIndex: 0, pageSize: 25},
  });
  const {start, length, setPagination} = tableState;

  const resetToFirstPage = () =>
    setPagination(prev => (prev.pageIndex === 0 ? prev : {...prev, pageIndex: 0}));

  const filters = {
    instanceId,
    userId,
    search: debouncedSearch.trim() || undefined,
    rating,
    reasons: reasons.length > 0 ? reasons : undefined,
    ...toUtcDayRange(dateRange),
  };

  const instanceResult = useAssistantFeedbackQuery(
    {...filters, start, length},
    {skip: scope !== 'instance'}
  );
  const globalResult = useGlobalAssistantFeedbackQuery(
    {...filters, start, length},
    {skip: scope !== 'global'}
  );
  const {data, isLoading, isFetching, isError, refetch} =
    scope === 'instance' ? instanceResult : globalResult;

  const [exportInstance, {isLoading: isExportingInstance}] =
    useExportAssistantFeedbackMutation();
  const [exportGlobal, {isLoading: isExportingGlobal}] =
    useExportGlobalAssistantFeedbackMutation();
  const isExporting = isExportingInstance || isExportingGlobal;

  const handleExport = async () => {
    try {
      const file = await (scope === 'instance'
        ? exportInstance(filters)
        : exportGlobal(filters)
      ).unwrap();
      if (!file) {
        setAlert('Failed to export feedback.', 'error');
        return;
      }
      fileDownload(file.data, file.fileName ?? exportFileName(new Date()));
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
  };

  const upCount = data?.upCount ?? 0;
  const downCount = data?.downCount ?? 0;
  const reasonCounts = data?.reasonCounts ?? [];
  const reasonOptions = FEEDBACK_REASONS.map(option => {
    const count =
      reasonCounts.find(entry => entry.reason === option.value)?.count ?? 0;
    return {
      value: String(option.value),
      label: `${option.label} (${formatCount(count)})`,
    };
  });

  const ratingValue = rating === undefined ? RATING_ALL : RATING_SLUGS[rating];
  const upSlug = RATING_SLUGS[AssistantFeedbackRatingEnum.Up];
  const downSlug = RATING_SLUGS[AssistantFeedbackRatingEnum.Down];

  const {table} = useTable({
    ...tableState,
    tableOptions: {
      manualPagination: true,
      manualSorting: true,
      manualFiltering: true,
      enableSorting: false,
      enableColumnFilters: false,
      columns,
      data: data?.data ?? EMPTY_ITEMS,
      getRowId: row => String(row.messageId),
      renderRowActions: ({row}) => (
        <TableRowActions>
          <TableRowActions.View
            tooltip="View conversation"
            onClick={() =>
              onViewConversation(row.original.conversationId, row.original.messageId)
            }
          />
        </TableRowActions>
      ),
      renderEmptyRowsFallback: () => (
        <Text c="dimmed" ta="center" py="xl" size="sm">
          No votes match these filters. Users rate Scout replies with the thumbs
          under each answer.
        </Text>
      ),
      renderTopToolbarCustomActions: () => (
        <TableToolbar>
          <TableToolbar.Actions>
            <TableToolbar.Action
              variant="export"
              loading={isExporting}
              tooltip="Downloads the newest 10,000 votes matching these filters"
              onClick={() => void handleExport()}
            />
          </TableToolbar.Actions>
          <TableToolbar.Filters>
            <SegmentedControl
              aria-label="Filter by rating"
              value={ratingValue}
              onChange={value => {
                setRating(ratingFromSlug(value));
                resetToFirstPage();
              }}
              data={[
                {
                  value: RATING_ALL,
                  label: `All (${formatCount(upCount + downCount)})`,
                },
                {value: upSlug, label: `Up (${formatCount(upCount)})`},
                {
                  value: downSlug,
                  label: `Down (${formatCount(downCount)})`,
                },
              ]}
            />
            <MultiSelect
              aria-label="Filter by reason"
              placeholder={reasons.length === 0 ? 'All reasons' : undefined}
              data={reasonOptions}
              value={reasons.map(String)}
              onChange={values => {
                setReasons(
                  values.map(value => Number(value) as AssistantFeedbackReasonEnum)
                );
                resetToFirstPage();
              }}
              clearable
              w={260}
            />
            <Box w={200}>
              <SelectBox
                placeholder="All users"
                ariaLabel="Filter by user"
                options={userOptions}
                value={userId !== undefined ? String(userId) : undefined}
                onSelectChangeCallback={value => {
                  setUserId(value ? Number(value) : undefined);
                  resetToFirstPage();
                }}
                wrapperProps={{mb: 0}}
              />
            </Box>
            <DatePickerInput
              type="range"
              placeholder="All dates"
              aria-label="Filter by vote date"
              valueFormat="MMM D, YYYY"
              clearable
              value={dateRange}
              onChange={next => {
                setDateRange(next);
                resetToFirstPage();
              }}
              w={240}
            />
            <Box w={240}>
              <Textbox
                ariaLabel="Search feedback"
                placeholder="Search comments, questions, users"
                value={search}
                onChangeCallback={event => {
                  setSearch(event.currentTarget.value);
                  resetToFirstPage();
                }}
                props={{mb: 0}}
              />
            </Box>
          </TableToolbar.Filters>
        </TableToolbar>
      ),
    },
    totalCount: data?.filteredRecords ?? 0,
    isError,
    isLoading,
    isFetching,
    onRefresh: refetch,
  });

  return table;
};
